import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'connected-react-router'
import { useParams } from 'react-router-dom'

import {
  clearSiteCache,
  clearSites,
  createSiteUser,
  createSiteUsers,
  fetchSite,
  fetchSiteAccessCodes,
  fetchSiteUsers,
  remindSiteUser,
  remindSiteUsers,
  resetBulkAddUsers,
  updateSiteUser,
} from 'store/sitesManager'
import {
  selectAccessCodesBySiteId,
  selectBulkAddUsersResponse,
  selectIsBulkOperationCompleted,
  selectSiteById,
  selectSiteUsersBySiteId,
  selectSiteUsersIsLoading,
  selectSitesIsLoading,
} from 'store/sitesManager/selector'
import {
  deleteReports,
  fetchHistoricalReports,
  fetchHsHistoricalReports,
  fetchLessonProgressReport,
  fetchTrainingReport,
} from 'store/reports'
import ErrorBoundary from 'components/ErrorBoundary'
import Loading from 'components/Loading/Loading'
import Site from './component'
import { filterCanceledUsers, UpdateSiteUserNotifications } from 'lib/util'
import { RootState, SiteUser } from 'types'
import { AppDispatch } from 'store/types'
import { useSiteNetSuiteAssets } from './hooks/useSiteNetSuiteAssets'

const SiteContainer: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { id } = useParams<{ id: string }>()
  const siteId = parseInt(id, 10)
  const [showEditModal, setShowEditModal] = useState(false)
  const siteNetSuiteAssets = useSiteNetSuiteAssets(siteId)

  // Combined selector to reduce re-renders
  const {
    accessCodes,
    addBulkUsersResponse,
    errorMessage,
    historicalReports,
    hsHistoricalReports,
    isBulkOperationCompleted,
    isFetchingHistoricalReports,
    isFetchingHsHistoricalReports,
    isFetchingReports,
    isLoadingSite,
    isLoadingSiteUsers,
    reports,
    site,
    siteUsers,
  } = useSelector((state: RootState) => {
    const sitesManagerState = state.sitesManager
    const reportsManagerState = state.reportsManager
    return {
      accessCodes: selectAccessCodesBySiteId(sitesManagerState, siteId),
      addBulkUsersResponse:
        selectBulkAddUsersResponse(sitesManagerState) || undefined,
      errorMessage: sitesManagerState?.error || null,
      historicalReports:
        reportsManagerState?.historicalReports?.historicalReportData?.[
          siteId
        ] || [],
      hsHistoricalReports:
        reportsManagerState?.historicalReports?.hsHistoricalReportData?.[
          siteId
        ] || [],
      isBulkOperationCompleted:
        selectIsBulkOperationCompleted(sitesManagerState) || false,
      isFetchingHistoricalReports:
        reportsManagerState?.historicalReports?.isFetching || false,
      isFetchingHsHistoricalReports:
        reportsManagerState?.historicalReports?.isFetchingHs || false,
      isFetchingReports: reportsManagerState?.isFetching || {},
      isLoadingSite: selectSitesIsLoading(sitesManagerState),
      isLoadingSiteUsers: selectSiteUsersIsLoading(sitesManagerState),
      reports: reportsManagerState?.reports || {},
      site: selectSiteById(sitesManagerState, siteId) || null,
      siteUsers: selectSiteUsersBySiteId(sitesManagerState, siteId) || null,
    }
  })

  // Memoize error object to prevent infinite loops
  const error = useMemo(() => {
    return errorMessage ? new Error(errorMessage) : undefined
  }, [errorMessage])

  useEffect(() => {
    if (siteId) {
      dispatch(fetchSiteAccessCodes(siteId))
    }
  }, [dispatch, siteId])

  const hasHistoricalReportsCached = useSelector((state: RootState) =>
    Object.prototype.hasOwnProperty.call(
      state.reportsManager?.historicalReports?.historicalReportData || {},
      siteId,
    ),
  )

  const hasHsHistoricalReportsCached = useSelector((state: RootState) =>
    Object.prototype.hasOwnProperty.call(
      state.reportsManager?.historicalReports?.hsHistoricalReportData || {},
      siteId,
    ),
  )

  useEffect(() => {
    if (!siteId || Number.isNaN(siteId)) return
    if (!hasHistoricalReportsCached && !isFetchingHistoricalReports) {
      dispatch(fetchHistoricalReports(siteId))
    }
  }, [
    dispatch,
    hasHistoricalReportsCached,
    isFetchingHistoricalReports,
    siteId,
  ])

  useEffect(() => {
    if (!siteId || Number.isNaN(siteId)) return
    if (!hasHsHistoricalReportsCached && !isFetchingHsHistoricalReports) {
      dispatch(fetchHsHistoricalReports(siteId))
    }
  }, [
    dispatch,
    hasHsHistoricalReportsCached,
    isFetchingHsHistoricalReports,
    siteId,
  ])

  // Fetch data when needed
  useEffect(() => {
    if (!site && !isLoadingSite && !errorMessage && siteId) {
      dispatch(fetchSite(siteId))
    }
    if (!siteUsers && !isLoadingSiteUsers && !errorMessage && siteId) {
      dispatch(fetchSiteUsers(siteId))
    }
  }, [
    dispatch,
    errorMessage,
    isLoadingSite,
    isLoadingSiteUsers,
    site,
    siteId,
    siteUsers,
  ])

  useEffect(() => {
    return () => {
      dispatch(deleteReports())
    }
  }, [dispatch])

  const backToSearch = useCallback(() => {
    dispatch(clearSites())
    dispatch(push('/css-admin/sites'))
  }, [dispatch])

  const handleGrantNewRole = useCallback(
    (siteUser: SiteUser, newRole: string) => {
      dispatch(
        updateSiteUser(
          { ...siteUser, roles: [newRole] },
          UpdateSiteUserNotifications.grantNewRole,
        ),
      )
    },
    [dispatch],
  )

  const handleCancelUserAccess = useCallback(
    (siteUser: SiteUser) => {
      dispatch(
        updateSiteUser(
          { ...siteUser, isActive: false },
          UpdateSiteUserNotifications.removeUser,
        ),
      )
    },
    [dispatch],
  )

  const handleClearSiteCache = () => {
    dispatch(clearSiteCache(site?.id))
  }

  const filteredSiteUsers = useMemo(() => {
    return siteUsers ? filterCanceledUsers(siteUsers) : null
  }, [siteUsers])

  const isLoading = isLoadingSite || isLoadingSiteUsers

  return (
    <ErrorBoundary>
      <Loading isLoading={isLoading} message="Loading Site" />
      <Site
        accessCodes={accessCodes}
        addBulkUsersResponse={addBulkUsersResponse}
        backToSearch={backToSearch}
        createSiteUser={(siteUser, sendEmail, selectedSite) =>
          dispatch(createSiteUser(siteUser, sendEmail, selectedSite))
        }
        createSiteUsers={(siteId, users, sendEmail) =>
          dispatch(createSiteUsers(siteId, users, sendEmail))
        }
        error={error}
        fetchLessonProgressReport={(siteId, skuCode) =>
          dispatch(fetchLessonProgressReport(siteId, skuCode))
        }
        fetchTrainingReport={(emails, skuCode, siteName) =>
          dispatch(fetchTrainingReport(emails, skuCode, siteName))
        }
        handleClearSiteCache={handleClearSiteCache}
        handleCancelUserAccess={handleCancelUserAccess}
        handleGrantNewRole={handleGrantNewRole}
        historicalReports={historicalReports}
        hsHistoricalReports={hsHistoricalReports}
        isBulkOperationCompleted={isBulkOperationCompleted}
        isFetchingHistoricalReports={isFetchingHistoricalReports}
        isFetchingHsHistoricalReports={isFetchingHsHistoricalReports}
        isFetchingReports={isFetchingReports}
        remindSiteUser={siteUser => dispatch(remindSiteUser(siteUser))}
        remindSiteUsers={(siteId, emailFilters) =>
          dispatch(remindSiteUsers(siteId, emailFilters))
        }
        reports={reports}
        resetBulkAddUsers={() => dispatch(resetBulkAddUsers())}
        showEditModal={showEditModal}
        site={site}
        siteUsers={filteredSiteUsers}
        siteNetSuiteAssets={siteNetSuiteAssets}
        toggleEditModal={() => setShowEditModal(prev => !prev)}
      />
    </ErrorBoundary>
  )
}

export default SiteContainer
